import { useLayoutEffect, useMemo, useRef } from 'react';
import { keyframes } from '@emotion/react';
import { Box, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { CORE_DAYS, formatTime } from '../../shared/taxonomy';
import { endMinute, startMinute, type Opportunity } from '../../shared/opportunity';
import Icon from '../../components/Icon';
import { notchMask } from '../../components/notch';
import { layoutSpans, seamEnd } from './layout';
import { attendedSpan, type Trims } from './attendance';
import { useIsMobile } from '../../shared/useIsMobile';
import { DAY_END_MIN, DAY_START_MIN, SPAN_MIN } from './DaySky';
import { isPersonal } from './personalEvents';
import { CONFLICT, TRAVEL_BORDER_WIDTH, travelBorder, travelFill } from './travelStyle';
import { buildDayPlan } from './conflicts';
import type { ColorKey } from './colorBy';
import { colorFor } from './colorBy';

/**
 * The week at a glance: seven columns, each a day painted as a sky.
 *
 * The gradient is the same time-of-day metaphor the card icons and the sidebar
 * filter use — dawn at the top of the column, night at the bottom — so a block's
 * vertical position reads as a time before you look at any label.
 *
 * Scheduled events are full-opacity parchment; Save for Later sits at 50%, per
 * the spec. Overlapping blocks share the column width rather than stacking, so
 * a double-booking is visible as a split rather than hidden underneath.
 */

/**
 * Every column shares the detailed day's vertical scale — 5am to midnight —
 * rather than a window fitted to whatever the user has scheduled. Two views of
 * the same day that put the same event at different heights are two different
 * days as far as the eye is concerned, and the week grid's whole job is to be
 * the day view seen from further away.
 *
 * The hour rules follow from that: one per hour, at the same fractions of the
 * column as `HourGrid` draws them, and at the same fractions the blocks below
 * are positioned against.
 */
const HOURS: number[] = [];
for (let h = DAY_START_MIN / 60; h <= DAY_END_MIN / 60; h += 1) HOURS.push(h * 60);

/** Where a minute sits in the column, as a percentage, clamped to the day. */
const pct = (minutes: number) =>
  ((Math.min(Math.max(minutes, DAY_START_MIN), DAY_END_MIN) - DAY_START_MIN) / SPAN_MIN) * 100;

/**
 * Minutes a block gives up when another starts where it ends. Ten of them are
 * about five pixels at this scale, which is the width of a visible seam and
 * far below the width of a legible time.
 */
const SEAM_MINUTES = 10;

/**
 * The eight core days (20th–27th) fill the available width. Pre-week and
 * post-week days keep that same column width and sit outside the viewport, so
 * adding a 10 September event scrolls into view rather than squeezing the whole
 * week narrower.
 */
const Scroller = styled('div')({
  overflowX: 'auto',
  overscrollBehavior: 'contain',
  paddingBottom: 10,
  scrollbarWidth: 'thin',
});

const Grid = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  [theme.breakpoints.down('sm')]: { gap: 4 },
}));

const Column = styled('div', { shouldForwardProp: (p) => p !== '$width' })<{ $width: string }>(
  ({ $width }) => ({
    flex: `0 0 ${$width}`,
    minWidth: 0,
  }),
);

const DayHead = styled('button')(({ theme }) => ({
  appearance: 'none',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  width: '100%',
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'flex-start',
  gap: 8,
  padding: '0 2px 8px',
  color: BRAND.parchment,
  overflow: 'hidden',
  '&:hover span': { textDecoration: 'underline', textUnderlineOffset: 3 },
  // Forty pixels a column: the date and its weekday, and nothing else.
  [theme.breakpoints.down('sm')]: { gap: 3, padding: '0 0 5px', justifyContent: 'center' },
}));

/**
 * The ring the open day wears, shown twice on a day nobody has opened yet.
 *
 * On a phone the week is eight thumbnails and nothing about a thumbnail says
 * "tap me". Borrowing the ring that means "this is the open day" and flashing
 * it on Wednesday is the lesson in the same vocabulary as the answer: this is
 * what a chosen day looks like, and here is one to choose.
 */
const PULSE = keyframes`
  0% { opacity: 0; }
  50% { opacity: 1; }
  100% { opacity: 0; }
`;

/** Once per page load — a lesson repeated on every visit is an interruption. */
let dayTapTaught = false;

const Sky = styled('div', {
  shouldForwardProp: (p) => p !== '$open' && p !== '$flash',
})<{ $open?: boolean; $flash?: boolean }>(({ $open, $flash }) => ({
  position: 'relative',
  // Taller columns so the day reads at a comfortable size on a wide screen.
  height: 'min(58vh, 620px)',
  minHeight: 380,
  '@media (max-width: 599.95px)': { height: 272, minHeight: 0, borderRadius: 8 },
  borderRadius: 12,
  overflow: 'hidden',
  cursor: 'pointer',
  // dawn -> midday -> dusk -> night, top to bottom
  background:
    'linear-gradient(180deg, #9fb4cc 0%, #6d8fb8 16%, #5f86b4 42%, #a98a86 68%, #b5766a 78%, #6c5470 90%, #3b3350 100%)',
  // The open day is ringed rather than tinted: a tint would lie about the
  // blocks inside it, which are coloured by the lens.
  border: $open ? `2px solid ${BRAND.parchment}` : '1px solid rgba(243,238,231,0.22)',
  // Drawn as an overlay rather than by thickening the border, so the column
  // does not shift a pixel while it flashes.
  ...($flash
    ? {
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          border: `2px solid ${BRAND.parchment}`,
          pointerEvents: 'none',
          zIndex: 5,
          opacity: 0,
          animation: `${PULSE} 760ms ease-in-out 380ms 2 both`,
        },
      }
    : null),
}));

const HourLine = styled('div')<{ $top: number }>(({ $top }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  top: `${$top}%`,
  borderTop: '1px dashed rgba(243,238,231,0.28)',
  pointerEvents: 'none',
}));

const Block = styled('div', {
  shouldForwardProp: (p) =>
    p !== '$saved' && p !== '$fill' && p !== '$plain' && p !== '$square',
})<{
  $saved: boolean;
  $fill: string | null;
  /** A personal event: a plain rounded box, not a ticket. */
  $plain?: boolean;
  /**
   * A phone. At forty pixels wide a notched corner is not a ticket, it is a
   * chewed edge — the shape needs room to read as a shape, and there is none.
   */
  $square?: boolean;
}>(({ $saved, $fill, $plain, $square }) => ({
  position: 'absolute',
  // The chosen color fills the whole card rather than sitting as a stripe.
  background: $fill ?? BRAND.parchment,
  color: BRAND.mocha,
  opacity: $saved ? 0.5 : 1,
  padding: '4px 6px',
  paddingRight: $saved ? 16 : 6,
  overflow: 'hidden',
  fontFamily: FONTS.body,
  fontSize: 9,
  fontWeight: 600,
  lineHeight: 1.15,
  ...($square ? { borderRadius: 0 } : $plain ? { borderRadius: 5 } : notchMask({ radius: 5 })),
}));

/**
 * Every column reserves the same height for all-day events, so adding one to a
 * single day doesn't shunt that day's sky out of alignment with the other six.
 * The height comes from the busiest day in the view.
 */
/**
 * Travel gaps, shown here as bare boxes. The week grid is a density read, not a
 * reading surface, so they carry no text — the colors alone say "you are moving
 * here" and, in red, "you cannot make this". The detailed day spells it out.
 */
const TravelBlock = styled('div')({
  position: 'absolute',
  borderRadius: 4,
  background: travelFill(),
  border: `${TRAVEL_BORDER_WIDTH}px dashed ${travelBorder()}`,
  pointerEvents: 'none',
});

const AllDayLane = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  marginBottom: 6,
});

/**
 * Marks a week-grid card as saved for later rather than scheduled.
 *
 * Half opacity carried that distinction on its own, which works beside a
 * full-opacity neighbour and not at all on a day where everything is saved —
 * there is nothing to compare against. The mark is absolute so it never
 * reflows a title, and sized to the text it sits beside.
 */
const SavedMark = styled('span')({
  position: 'absolute',
  top: 3,
  right: 4,
  display: 'flex',
  // Matches the card's own 9px type, so it reads as part of the line.
  fontSize: 9,
  lineHeight: 1,
  color: 'rgba(54,47,42,0.75)',
  pointerEvents: 'none',
});

const AllDayChip = styled('div', { shouldForwardProp: (p) => p !== '$saved' })<{ $saved: boolean }>(({ $saved }) => ({
  background: 'rgba(243,238,231,0.9)',
  color: BRAND.mocha,
  opacity: $saved ? 0.5 : 1,
  borderRadius: 4,
  padding: '2px 6px',
  fontFamily: FONTS.body,
  fontSize: 9,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  border: '1px dashed rgba(54,47,42,0.45)',
}));

interface Props {
  /** Days to render, in order. Includes pre-week days when they hold events. */
  days: { key: string; label: string; weekday: string }[];
  eventsByDay: Map<string, Opportunity[]>;
  savedIds: Set<string>;
  colorBy: ColorKey;
  /** How much of each event the user is attending. Blocks are drawn to it. */
  trims: Trims;
  /** The day showing in detail, drawn as the selected column. */
  openDay?: string | null;
  onOpenDay: (dayKey: string) => void;
}

export default function WeekGrid({
  days,
  eventsByDay,
  savedIds,
  colorBy,
  trims,
  openDay = null,
  onOpenDay,
}: Props) {
  /**
   * On a phone the week is a thumbnail of itself.
   *
   * Eight columns across 375px is 40px a column, and 40px of parchment cannot
   * hold a word — so it holds none, and the blocks say what they have always
   * said at a glance: how full the day is, when it is busy, and where it
   * collides. The detail is one tap below.
   */
  const mobile = useIsMobile();

  /** Wednesday of Climate Week, flashed once on a phone to show a day is tappable. */
  const flashDay =
    mobile && !openDay && !dayTapTaught
      ? (CORE_DAYS.find((d) => d.weekday === 'Wednesday')?.key ?? null)
      : null;
  const scroller = useRef<HTMLDivElement | null>(null);
  const firstCore = useRef<HTMLDivElement | null>(null);

  /**
   * Opens on Climate Week itself.
   *
   * Pre-week days share the row so that adding a 10 September event does not
   * squeeze the eight days that matter — but they sit to the left of them, so
   * the row's natural resting position is a week nobody came here for. This
   * scrolls past them on arrival, and again whenever the set of days changes.
   * A user's own sideways scrolling is left alone: this fires on the day set,
   * not on every render.
   */
  const dayKeys = days.map((d) => d.key).join();
  useLayoutEffect(() => {
    const node = scroller.current;
    const core = firstCore.current;
    if (node && core) node.scrollLeft = core.offsetLeft;
  }, [dayKeys]);
  // The eight core days divide the available width between them, so the
  // calendar grows with the window instead of sitting at a fixed size. Pre- and
  // post-week days take that same width and are reached by scrolling sideways.
  const CORE_COUNT = 8;
  // Matches the Grid's own gap, which a phone tightens to buy column width.
  const GAP = mobile ? 4 : 12;
  const columnWidth = `calc((100% - ${(CORE_COUNT - 1) * GAP}px) / ${CORE_COUNT})`;
  // One reserved height for the all-day lane across every column.
  const allDayRows = useMemo(() => {
    let most = 0;
    for (const list of eventsByDay.values()) {
      most = Math.max(most, list.filter((e) => e.isAllDay).length);
    }
    return most;
  }, [eventsByDay]);
  const laneHeight = allDayRows === 0 ? 0 : allDayRows * 17 + (allDayRows - 1) * 3 + 6;

  return (
    <Scroller ref={scroller} data-own-swipe>
    <Grid style={{ minWidth: '100%' }}>
      {days.map((day) => {
        const events = eventsByDay.get(day.key) ?? [];
        // Attended hours, here as in the day view: the two must agree about
        // what overlaps, or the count under a column contradicts the column.
        const plan = buildDayPlan(day.key, events, (e) => attendedSpan(e, trims));
        const lanes = layoutSpans(
          plan.timed.map(({ event }) => ({ id: event.id, ...attendedSpan(event, trims), event })),
        );
        const starts = lanes.map((l) => l.item.start);

        return (
          <Column
            key={day.key}
            $width={columnWidth}
            ref={day.key === CORE_DAYS[0].key ? firstCore : undefined}
          >
            <DayHead onClick={() => onOpenDay(day.key)} aria-label={`Open ${day.weekday}`}>
              <span
                style={{
                  fontFamily: FONTS.serif,
                  fontSize: mobile ? 15 : 21,
                  fontWeight: 700,
                }}
              >
                {day.label}
              </span>
              <span
                style={{
                  fontFamily: FONTS.serif,
                  fontSize: mobile ? 9.5 : 13,
                  opacity: 0.75,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {mobile ? day.weekday.slice(0, 3) : day.weekday}
              </span>
            </DayHead>

            <AllDayLane style={{ height: laneHeight }}>
              {plan.allDay.map((e) => (
                <Tooltip key={e.id} title={`${e.name} — no time published`}>
                  <AllDayChip $saved={savedIds.has(e.id)}>{e.name}</AllDayChip>
                </Tooltip>
              ))}
            </AllDayLane>

            <Sky
              $open={day.key === openDay}
              $flash={day.key === flashDay}
              onAnimationEnd={
                day.key === flashDay
                  ? () => {
                      dayTapTaught = true;
                    }
                  : undefined
              }
              onClick={() => {
                // Whoever taps a day has learned what the flash was teaching.
                dayTapTaught = true;
                onOpenDay(day.key);
              }}
              role="button"
              aria-pressed={day.key === openDay}
              aria-label={`${day.weekday} schedule`}
            >
              {HOURS.map((m) => (
                <HourLine key={m} $top={pct(m)} />
              ))}

              {/* Behind the event blocks, so a card always wins the overlap. */}
              {plan.timed.map(({ event, travelFromPrevious }, i) => {
                if (!travelFromPrevious || i === 0) return null;
                const prev = plan.timed[i - 1].event;
                const gapStart = endMinute(prev) ?? (startMinute(prev) ?? 0) + 60;
                const gapEnd = startMinute(event) ?? gapStart;
                const top = pct(gapStart);
                const bottom = pct(gapEnd);
                if (bottom <= top) return null;
                return (
                  <TravelBlock
                    key={`travel-${event.id}`}
                    style={{
                      top: `${top}%`,
                      height: `${Math.max(bottom - top, 1.1)}%`,
                      left: 3,
                      right: 3,
                    }}
                  />
                );
              })}

              {lanes.map(({ item, lane, lanes: total }) => {
                const { event } = item;
                const top = pct(item.start);
                // A few minutes short when the next block starts where this one
                // ends, so back-to-back events read as two blocks.
                const height = Math.max(
                  pct(seamEnd(item.end, starts, SEAM_MINUTES)) - top,
                  3.2,
                );
                const width = 100 / total;

                return (
                  <Tooltip
                    key={event.id}
                    title={`${formatTime(startMinute(event))} · ${event.name}`}
                    placement="top"
                  >
                    <Block
                      $saved={savedIds.has(event.id)}
                      /* The user's own events read as their own thing here too:
                         a plain box rather than a ticket, and always parchment,
                         since the colour lenses sort by what the dataset knows
                         about an event and this one has none of that. */
                      $plain={isPersonal(event)}
                      $square={mobile}
                      $fill={isPersonal(event) ? null : colorFor(event, colorBy)}
                      style={{
                        top: `${top}%`,
                        height: `${height}%`,
                        left: `calc(${lane * width}% + 3px)`,
                        width: `calc(${width}% - 6px)`,
                      }}
                    >
                      {savedIds.has(event.id) && !mobile && (
                        <SavedMark aria-hidden="true">
                          <Icon name="ui:saveOn" size="1em" />
                        </SavedMark>
                      )}
                      {!mobile && event.name}
                    </Block>
                  </Tooltip>
                );
              })}

              {events.length === 0 && !mobile && (
                <Typography
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 11,
                    color: 'rgba(255,255,255,0.72)',
                    px: 2,
                    textAlign: 'center',
                  }}
                >
                  Nothing yet
                </Typography>
              )}
            </Sky>

            <Box sx={{ textAlign: 'center', pt: 0.8 }}>
              <Typography
                sx={{
                  fontSize: mobile ? 8 : 10.5,
                  color: 'rgba(243,238,231,0.55)',
                  fontWeight: 600,
                  lineHeight: 1.35,
                  whiteSpace: 'nowrap',
                }}
              >
                {events.length === 0
                  ? '—'
                  : `${events.length} ${events.length === 1 ? 'event' : 'events'}`}
                {plan.overlaps.length > 0 && (
                  <Box
                    component="span"
                    // Its own line: a clash is not a footnote to the count.
                    sx={{
                      display: 'block',
                      color: CONFLICT,
                      fontFamily: FONTS.body,
                      fontWeight: 700,
                    }}
                  >
                    {plan.overlaps.length}{' '}
                    {plan.overlaps.length === 1 ? 'conflict' : 'conflicts'}
                  </Box>
                )}
              </Typography>
            </Box>
          </Column>
        );
      })}
    </Grid>
    </Scroller>
  );
}
